import { CSSProperties, FC, JSX, useEffect, useMemo, useState } from "react";
import { iconSvgAttributes } from "./iconSvgAttributes";
import { iconLookup } from "./iconLookup";
import { sharedLineIconProps } from "./interfaces";

interface BaseIconProps extends React.SVGAttributes<SVGElement> {
  /**
   * Any particular fill color for the entire icon.
   * This can also be applied as "fill" in scss, or "color" in scss.
   * The fill defaults to "currentColor", or whatever the text around it is.
   */
  color?: string;
  /**
   * A text description for the icon. If the icon is not decorative,
   * this will be placed in the icon's <title> child. It's like an
   * image's alt text.
   * This should describe the action of the icon, if applicable, instead of 
   * the appearance of the icon.
   */
  description?: string;
  /**
   * The desired height of the icon. Icons will maintain their internal aspect ratios.
   */
  height?: string;
  /**
   * The name of the icon, preferably from the QesIcons enum.
   */
  iconName: string;
  /**
   * A decorative icon - one that doesn't convey any meaning on its own,
   * or lives alongside text that conveys its meaning - will be hidden 
   * from screen readers and will not have an internal text description.
   * The only valid values are true or undefined.
   */
  isDecorative?: boolean;
  /**
   * CSS properties to apply to the <path> element inside the svg
   */
  pathStyle?: CSSProperties;
  /**
   * Hopefully-unique id used to differentiate it and its ids 
   * from others on the page.
   * Leaving this optional because icons, on their own, should not be interactive.
   */
  testId?: string;
  /**
   * The desired width of the icon. Icons will maintain their internal aspect ratios.
   */
  width?: string;
};

// description is required
export interface QesIconWithDescription extends BaseIconProps {
  description: string;
}

// or isDecorative is required (to be true)
export interface QesIconIsDecorative extends BaseIconProps {
  isDecorative: true;
}

type IconProps = QesIconWithDescription | QesIconIsDecorative;

/**
 * The easiest way to render an approved svg icon in QES. The only required props are iconName and one of 
 * description or isDecorative. If both are provided, isDecorative takes precedence. <br />
 * All of our design system icons are 24px squares by default. Specifing one of height or width will maintain that aspect ratio. <br />
 * A testId should be provided if the icon itself is interactive, but in most cases you should wrap the icon in a button for that. <br />
 */
export const QesIcon: FC<IconProps> = (props: IconProps) => {
  // extract our custom props so they don't get spread onto the HTML element
  const { className, color, description, height, iconName, isDecorative, pathStyle, testId, width, ...otherProps } = props;
  const sharedSvgAttributes = useMemo(() => {
    const classNames = "qes-icon " + (className || "");
    return iconSvgAttributes(testId, classNames, isDecorative);
  }, [color, testId, className, isDecorative]);

  const { svgCode, svgProps = { height: "24", width: "24" } } = iconLookup.get(iconName);
  const [calculatedHeight, setCalculatedHeight] = useState<string>();
  const [calculatedWidth, setCalculatedWidth] = useState<string>();

  const svgBody = useMemo(() => {
    return svgCode(color, pathStyle);
  }, [svgCode, testId, color, pathStyle]);

  /** 
   * Adjust the dimensions as necessary depending on what was provided.
   * The general formula here is (height1 / width1) = (height2 / width2).
   * 1. If there's a width and no height, just calculate a height according to its aspect ratio.
   * 2. If there's a height and no width, just calculate a width according to its aspect ratio.
   * 3. If there's neither, determine whether height or width is larger. Set that dimension to 1em (current font size),
   *    and calculate a value for the other dimension according to its aspect ratio.
   * 4. If both are provided, just use those. This mostly applies when dimensions are added dynamically and the 
   *    calculated values need to be removed, like in storybook.
   */
  useEffect(() => {
    const h = svgProps.height as string;
    const w = svgProps.width as string;
    if (!!width && !height) {
      setCalculatedHeight(((parseFloat(h) / parseFloat(w)) * parseFloat(width)).toString());
    } else if (!width && !!height) {
      setCalculatedWidth((parseFloat(height) / (parseFloat(h) / parseFloat(w))).toString())
    } else if (!width && !height) {
      if (parseFloat(h) > parseFloat(w)) {
        const newHeight = 1.1; // default 1em;
        const newCalculatedWidth = newHeight / (parseFloat(h) / parseFloat(w));
        setCalculatedWidth(`${newCalculatedWidth}em`);
        setCalculatedHeight(`${newHeight}em`);
      } else {
        const newWidth = 1.1;
        const newCalculatedHeight = ((parseFloat(h) / parseFloat(w)) * newWidth);
        setCalculatedHeight(`${newCalculatedHeight}em`);
        setCalculatedWidth(`${newWidth}em`);
      }
    } else if (!!width && !!height) {
      setCalculatedHeight(undefined);
      setCalculatedWidth(undefined);
    }
  }, [width, height, svgProps.height, svgProps.width]);

  if (svgBody.hasOwnProperty("length")) {
    return (
      <svg
        {...sharedSvgAttributes}
        {...svgProps}
        {...otherProps}
        width={calculatedWidth || width || "1em"}
        height={calculatedHeight || height || "1em"}
        viewBox={`0 0 ${svgProps.width} ${svgProps.height}`}
      >
        {!isDecorative && !!description && <title>{description}</title>}
        {(svgBody as string[]).map((d, index) => (
          <path
            key={index}
            d={d}
            {...sharedLineIconProps(color, pathStyle)}
          />
        ))}
      </svg>
    )
  }

  return (
    <svg
      {...sharedSvgAttributes}
      {...svgProps}
      {...otherProps}
      width={calculatedWidth || props.width || "1em"}
      height={calculatedHeight || height || "1em"}
      viewBox={`0 0 ${svgProps.width} ${svgProps.height}`}
    >
      {!isDecorative && !!description && <title>{description}</title>}
      {svgBody as JSX.Element}
    </svg>
  );
}
